import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { XERO_DEALER_ACCOUNTS, XeroAccountOption } from '../config/xero.config';

/**
 * Returns the list of Xero GL accounts to show in the GL dropdown.
 *
 * - Reads from `xero_accounts` table (cached COA pulled from the user's
 *   Xero org once OAuth is connected).
 * - If `xero_accounts` is empty for the org, falls back to the hardcoded
 *   AU dealer chart in `xero.config.ts` so the app works pre-Xero.
 * - Filters to ACTIVE accounts only.
 *
 * Shape matches `XeroAccountOption` so existing consumers (Autocomplete) don't change.
 */
export function useXeroAccounts(organizationId?: string | null) {
  const [accounts, setAccounts] = useState<XeroAccountOption[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!organizationId) {
      setAccounts(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      const { data, error } = await supabase
        .from('xero_accounts')
        .select('code, name, type, status')
        .eq('organization_id', organizationId)
        .eq('status', 'ACTIVE')
        .order('code');
      if (cancelled) return;
      if (!error && data && data.length > 0) {
        setAccounts(
          data.map((a: any) => ({
            code: a.code,
            name: a.name,
            category: mapXeroTypeToCategory(a.type),
          }))
        );
      } else {
        setAccounts(null); // signal "use fallback"
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [organizationId]);

  const list = useMemo<XeroAccountOption[]>(() => {
    return accounts && accounts.length > 0 ? accounts : XERO_DEALER_ACCOUNTS;
  }, [accounts]);

  return { accounts: list, isLive: !!(accounts && accounts.length > 0), loading };
}

function mapXeroTypeToCategory(xeroType?: string): XeroAccountOption['category'] {
  switch (xeroType) {
    case 'REVENUE':
    case 'SALES':
    case 'OTHERINCOME':           return 'Revenue';
    case 'DIRECTCOSTS':           return 'Direct Cost';
    case 'EXPENSE':
    case 'OVERHEADS':             return 'Operating Expense';
    case 'CURRENT':
    case 'CURRENTASSET':
    case 'INVENTORY':
    case 'FIXEDASSET':
    case 'NONCURRENT':            return 'Asset';
    case 'CURRLIAB':
    case 'LIABILITY':
    case 'NONCURRLIAB':            return 'Liability';
    default:                       return 'Operating Expense';
  }
}
